import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  Download,
  ArrowUpCircle,
  ArrowDownCircle,
  Coins,
  ChevronLeft,
  ChevronRight,
  Trash2,
} from "lucide-react";
import { camelizeKeys } from "../../../../../utils/camelizeKeys";
import { useToast } from "../../../../../components/ui/toast/ToastContext";

const getDateRangeStatus = (from, to) => {
  if (!from && !to) return "empty";
  if (!from || !to) return "partial";
  if (from > to) return "invalid";
  return "valid";
};

const formatCurrency = (value) => {
  if (value == null) return "R$ 0,00";
  const abs = Math.abs(value);
  const [int, dec] = abs.toFixed(2).replace(".", ",").split(",");
  const formatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const prefix = value < 0 ? "- " : "";
  return `${prefix}R$\u00a0${formatted},${dec}`;
};

const CustomCheckbox = ({ checked, onChange, label, colorScheme = "teal" }) => {
  const activeColors =
    colorScheme === "red"
      ? "bg-redSoft border-redSoft"
      : "bg-tealMedium border-tealMedium";

  const labelColors = colorScheme === "red" ? "text-redSoft" : "text-tealDark";

  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <div
        onClick={onChange}
        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${checked
            ? activeColors
            : "bg-white border-bluePale hover:border-tealMedium"
          }`}
      >
        {checked && (
          <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
            <path
              d="M1 3.5L3.5 6L8 1"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      {label && (
        <span
          className={`text-sm font-medium ${checked ? labelColors : "text-blueDarker/50"}`}
        >
          {label}
        </span>
      )}
    </label>
  );
};

const MovementRow = ({ movement }) => {
  const isDebit = movement.launchType === "DÉBITO";

  return (
    <div
      className={`grid items-center gap-4 px-5 py-4 rounded-xl text-sm text-blueDarker ${isDebit ? "bg-red-50" : "bg-blueLight"}`}
      style={{ gridTemplateColumns: "28px 1fr 130px 160px 160px" }}
    >
      <div className="flex items-center justify-center">
        {isDebit ? (
          <ArrowDownCircle
            size={20}
            strokeWidth={1.75}
            className="text-redSoft"
          />
        ) : (
          <ArrowUpCircle
            size={20}
            strokeWidth={1.75}
            className="text-tealBase"
          />
        )}
      </div>

      <span
        className={`font-semibold truncate cursor-pointer hover:underline ${isDebit ? "text-redSoft" : "text-tealBase"}`}
      >
        {movement.movementHistory}
      </span>

      <span className="text-blueDarker/70">{movement.formattedDate}</span>

      <span
        className={`font-semibold ${isDebit ? "text-redSoft" : "text-tealDark"}`}
      >
        {formatCurrency(movement.grossValue)}
      </span>

      <span className="font-medium text-blueDarker">
        {formatCurrency(movement.runningBalance)}
      </span>
    </div>
  );
};

const Statement = ({ apiClientId }) => {
  const { addToast } = useToast();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showDebit, setShowDebit] = useState(true);
  const [showCredit, setShowCredit] = useState(true);
  const [movements, setMovements] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [updatedAt, setUpdatedAt] = useState("");
  const [loading, setLoading] = useState(false);
  const initialRender = useRef(true);

  const dateRangeStatus = getDateRangeStatus(dateFrom, dateTo);
  const effectiveDateFrom = dateRangeStatus === "valid" ? dateFrom : "";
  const effectiveDateTo = dateRangeStatus === "valid" ? dateTo : "";

  const buildParams = (page) => {
    const params = new URLSearchParams();
    if (page) params.set("page", page);
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (effectiveDateFrom) params.set("date_from", effectiveDateFrom);
    if (effectiveDateTo) params.set("date_to", effectiveDateTo);
    if (showDebit && !showCredit) params.set("launch_type", "DÉBITO");
    if (!showDebit && showCredit) params.set("launch_type", "CRÉDITO");
    return params;
  };

  const fetchMovements = async (page = 1) => {
    if (!showDebit && !showCredit) {
      setMovements([]);
      setPagination(null);
      setUpdatedAt("");
      return;
    }

    setLoading(true);

    try {
      const params = buildParams(page);
      params.set("per_page", "10");

      const res = await fetch(
        `/app/analyst/client/${apiClientId}/dashboard/movements?${params}`,
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Erro ao carregar movimentações");
      }

      const data = camelizeKeys(await res.json());

      setMovements(data.movements || []);
      setPagination(data.pagination || null);
      setUpdatedAt(data.updatedAt || "");
    } catch (err) {
      addToast({ type: "error", message: err.message });
      setMovements([]);
      setPagination(null);
      setUpdatedAt("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovements();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    fetchMovements(1);
  }, [debouncedSearch, showDebit, showCredit, effectiveDateFrom, effectiveDateTo]);

  const handleClearDateFilter = () => {
    setDateFrom("");
    setDateTo("");
  };

  const handlePageChange = (page) => {
    if (!pagination || page < 1 || page > pagination.totalPages) return;
    fetchMovements(page);
  };

  const handleExport = () => {
    const params = buildParams();

    window.open(
      `/app/analyst/client/${apiClientId}/dashboard/movements/export?${params}`,
      "_blank",
    );
  };

  const totalCount = pagination?.totalCount || 0;

  return (
    <div className="bg-white rounded-custom20 p-4 h-full flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <div
          className="bg-blueLight flex flex-row items-center gap-10 px-4 py-3"
          style={{ borderRadius: "12px" }}
        >
          <div className="flex items-center gap-3 flex-shrink-0">
            <Coins className="text-tealBase" size={20} />

            <span className="text-blueDarker text-xl font-semibold whitespace-nowrap">
              Extrato{" "}
              <span className="font-normal">
                ({totalCount} movimentações)
              </span>
            </span>
          </div>

          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Pesquisar movimentação..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 rounded-xl border border-bluePale bg-white text-sm text-blueDarker placeholder:text-blueDarker/40 focus:outline-none focus:border-tealMedium transition-colors"
            />
            <Search
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-tealBase pointer-events-none"
            />
          </div>

          <div className="h-8 w-px bg-bluePale flex-shrink-0" />

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={`w-36 px-4 py-2.5 rounded-xl border bg-white text-sm text-blueDarker focus:outline-none focus:border-tealMedium transition-colors flex-shrink-0 ${
              dateRangeStatus === "invalid" ? "border-redSoft" : "border-bluePale"
            }`}
          />

          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={`w-36 px-4 py-2.5 rounded-xl border bg-white text-sm text-blueDarker focus:outline-none focus:border-tealMedium transition-colors flex-shrink-0 ${
              dateRangeStatus === "invalid" ? "border-redSoft" : "border-bluePale"
            }`}
          />

          <button
            type="button"
            onClick={handleClearDateFilter}
            title="Limpar filtro de período"
            className="flex items-center justify-center p-2.5 rounded-xl bg-redSoft hover:opacity-90 transition-opacity text-white border-r-2 border-b-2 border-redDeep flex-shrink-0"
          >
            <Trash2 size={16} />
          </button>
        </div>

        {dateRangeStatus === "invalid" && (
          <span className="text-redSoft text-xs px-1">
            A data inicial não pode ser posterior à data final.
          </span>
        )}
        {dateRangeStatus === "partial" && (
          <span className="text-blueDarker/50 text-xs px-1">
            Selecione as duas datas para filtrar por período.
          </span>
        )}
      </div>

      <div className="flex flex-col gap-0">
        <div
          className="grid items-center gap-4 px-5 py-2 text-base font-semibold text-blueDarker"
          style={{ gridTemplateColumns: "28px 1fr 130px 160px 160px" }}
        >
          <div />

          <div className="flex items-center gap-4">
            <span>Últimas movimentações</span>
            <div className="flex items-center gap-3">
              <CustomCheckbox
                checked={showDebit}
                onChange={() => setShowDebit((v) => !v)}
                label="Débito"
                colorScheme="red"
              />
              <CustomCheckbox
                checked={showCredit}
                onChange={() => setShowCredit((v) => !v)}
                label="Crédito"
                colorScheme="teal"
              />
            </div>
          </div>

          <span>Data</span>
          <span>Movimentação</span>
          <span>Saldo</span>
        </div>

        <div className="flex flex-col gap-2 mt-1">
          {loading ? (
            <div className="text-center text-blueDarker/50 py-10 text-sm">
              Carregando...
            </div>
          ) : movements.length === 0 ? (
            <div className="text-center text-blueDarker/50 py-10 text-sm">
              Nenhuma movimentação encontrada.
            </div>
          ) : (
            movements.map((m) => <MovementRow key={m.id} movement={m} />)
          )}
        </div>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-2">
          <button
            type="button"
            disabled={pagination.currentPage <= 1}
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-bluePale text-sm text-blueDarker disabled:opacity-40 hover:bg-blueLight transition-colors"
          >
            <ChevronLeft size={14} />
            Anterior
          </button>

          <span className="text-sm text-blueDarker/70">
            Página {pagination.currentPage} de {pagination.totalPages}
          </span>

          <button
            type="button"
            disabled={pagination.currentPage >= pagination.totalPages}
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-bluePale text-sm text-blueDarker disabled:opacity-40 hover:bg-blueLight transition-colors"
          >
            Próximo
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mt-auto pt-2">
        {updatedAt && (
          <span className="text-xs text-blueDarker/50">
            Última atualização: {updatedAt}
          </span>
        )}

        <button
          type="button"
          onClick={handleExport}
          className="flex items-center gap-2 bg-tealMedium hover:opacity-90 transition-opacity text-white font-bold rounded-xl px-5 py-2.5 text-sm border-r-2 border-b-2 border-tealBase"
        >
          <Download size={14} />
          EXPORTAR RELATÓRIO
        </button>
      </div>
    </div>
  );
};

export default Statement;
